import React from 'react';
import { Dumbbell, TrendingDown, Laptop, Trophy, Star, ArrowRight, Award, Heart, Target, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { trainerInfo, services, transformations, whyChooseMe } from '../mock';
import { BookingCalendar } from './BookingCalendar';
import { ImageCarousel } from './ImageCarousel';

const iconMap = {
  Dumbbell: Dumbbell,
  TrendingDown: TrendingDown,
  Laptop: Laptop,
  Trophy: Trophy
};

const aboutImages = [
"https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/ecpg1zjw_9a155de6-c0f7-43c6-a804-f48f19254798.jpg",
"https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/olajcmz3_WhatsApp%20Image%202026-02-03%20at%202.16.04%20PM.jpeg",
"https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/hbof5v83_WhatsApp%20Image%202026-01-30%20at%201.27.03%20PM.jpeg",
"https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/n4jp0hyp_WhatsApp%20Image%202026-01-30%20at%201.26.35%20PM.jpeg",
"https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/urz3f8y1_WhatsApp%20Image%202026-02-03%20at%202.13.51%20PM.jpeg",
"https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/tjg7zhzv_WhatsApp%20Image%202026-02-03%20at%202.13.42%20PM.jpeg"];


export const Home = () => {
  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="network-header">
        <div className="nav-wrapper">
          <div className="network-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/bv7uqtnu_Rig%20Solid%20Medium%20Fill.png"
              alt="V2 Fitness Logo"
              className="logo-image" />

            <span>{trainerInfo.name}</span>
          </div>
          <nav className="network-nav">
            <a href="#services" className="network-nav-link">Services</a>
            <a href="#transformations" className="network-nav-link">Results</a>
            <a href="#booking" className="network-nav-link">Book Free Session</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <Badge className="hero-badge">
            <Award size={16} />
            IFBB Pro Certified
          </Badge>
          <h1 className="display-large hero-title">{trainerInfo.tagline}</h1>
          <p className="hero-subtitle">{trainerInfo.heroSubtitle}</p>
          <p className="body-large hero-description">
            {trainerInfo.description}
          </p>
          <div className="hero-buttons">
            <Button className="btn-cta" onClick={scrollToBooking}>
              Start Your Transformation
              <ArrowRight size={20} />
            </Button>
            <Button className="btn-secondary" onClick={() => document.getElementById('transformations')?.scrollIntoView({ behavior: 'smooth' })}>
              See Real Results
            </Button>
          </div>
          
          {/* Stats */}
          <div className="hero-stats">
            {trainerInfo.stats.map((stat, index) =>
            <div key={index} className="stat-item">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="intro-section">
        <div className="container">
          <div className="intro-grid">
            <div className="intro-image">
              <ImageCarousel images={aboutImages} />
            </div>
            <div className="intro-content">
              <h2 className="heading-1">About Me | IFBB Pro Trainer in New York City</h2>
              <h3 className="heading-2 trainer-name">{trainerInfo.name}, IFBB Pro</h3>
              <div className="intro-story">
                <p className="body-medium intro-text">
                  I am an IFBB Pro Trainer, competitive bodybuilder, and one of the most experienced personal trainers in New York City, with over three decades of hands-on expertise in body transformation and physique development. I trained under an IFBB Pro and have won multiple bodybuilding competitions, shaping my deep understanding of how elite bodies are built, refined, and sustained.
                </p>
                <p className="body-medium intro-text">
                  Currently, I train clients at <strong>Complete Body Gym on 19th Street in Manhattan, NYC</strong>, where I am known for delivering some of the best transformation results in the city. I am proud to be recognized as one of the best Indian personal trainers in NYC, trusted by clients who demand high standards, accountability, and visible progress.
                </p>
                <p className="body-medium intro-text">
                  My core expertise includes complete body transformation, beach body preparation, fat loss, muscle definition, and physique sculpting. Every program I design is customized, data-driven, and aligned with your lifestyle, goals, and body type. I focus on intense yet intelligent training that maximizes results while minimizing wasted effort.
                </p>
                <div className="intro-columns">
                  <div className="intro-column">
                    <p className="body-medium intro-text">
                      As a celebrity trainer and online fitness coach, I also work with clients worldwide through structured online training programs. These include personalized workout plans, tailored meal plans, weekly progress tracking, and consistent follow-ups to ensure momentum and long-term success.
                    </p>
                  </div>
                  <div className="intro-column">
                    <p className="body-medium intro-text">
                      <strong>I believe transformation is not just physical. It is a mindset shift.</strong> My role is to push boundaries, unlock discipline, and help you achieve results you once thought were impossible. If you are searching for a professional personal trainer in Manhattan NYC who delivers proven results, elite coaching, and unmatched experience, I am here to guide you every step of the way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="credentials">
                <Badge className="credential-badge"><Heart size={14} /> IFBB Pro Card</Badge>
                <Badge className="credential-badge"><Target size={14} /> 30+ Years</Badge>
                <Badge className="credential-badge"><Zap size={14} /> 500+ Transformations</Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Me Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Why Train With Me?</h2>
            <p className="body-large">Six reasons why I'm the trainer you've been looking for</p>
          </div>
          <div className="why-choose-grid">
            {whyChooseMe.map((reason, index) => {
              const IconComponent = {
                Flame: Star,
                Target: Target,
                Zap: Zap,
                Trophy: Trophy,
                Globe: Laptop,
                Users: Heart
              }[reason.icon];

              return (
                <div key={index} className="why-card">
                  <div className="why-card-inner">
                    <div className="why-icon-wrapper">
                      <IconComponent className="why-icon" size={32} />
                    </div>
                    <div className="why-stat">{reason.stat}</div>
                    <h3 className="why-title">{reason.title}</h3>
                    <p className="why-description">{reason.description}</p>
                  </div>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Choose Your Transformation Path</h2>
            <p className="body-large">Every journey is unique. Find the program that speaks to your goals.</p>
          </div>
          
          <div className="services-grid">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="service-card network-card">
                  <CardHeader>
                    <div className="service-icon">
                      <IconComponent size={28} />
                    </div>
                    <div className="service-subtitle">{service.subtitle}</div>
                    <CardTitle className="network-card-title">{service.title}</CardTitle>
                    <CardDescription className="network-card-content">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="service-features">
                      {service.features.map((feature, idx) =>
                      <li key={idx}>
                          <Star size={16} />
                          <span>{feature}</span>
                        </li>
                      )}
                    </ul>
                    <div className="service-emotional">{service.emotional}</div>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Transformations Section */}
      <section className="transformations-section" id="transformations">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Real People. Real Stories. Real Results.</h2>
            <p className="body-large">These aren't just transformations. They're life changes. Yours could be next.</p>
          </div>
          
          <div className="transformations-grid">
            {transformations.map((transformation) =>
            <Card key={transformation.id} className="transformation-card network-card">
                <div className="transformation-image">
                  <img src={transformation.image} alt={transformation.clientName} />
                  <Badge className="transformation-badge">{transformation.program}</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="network-card-title">{transformation.clientName}</CardTitle>
                  <div className="achievement">{transformation.achievement}</div>
                </CardHeader>
                <CardContent>
                  <p className="testimonial">"{transformation.testimonial}"</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <BookingCalendar />

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Get In Touch</h2>
            <p className="body-large">Ready to start your transformation? Reach out today.</p>
          </div>
          
          <div className="contact-grid">
            <div className="contact-card">
              <div className="contact-icon-wrapper">
                <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="contact-title">Email</h3>
              <a href="mailto:v2fitness@gmail.com" className="contact-link">Ifbbvikasvaid@gmail.com</a>
            </div>

            <div className="contact-card">
              <div className="contact-icon-wrapper">
                <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="contact-title">USA Contact</h3>
              <a href="tel:+16462759747" className="contact-link">+1 (646) 275-9747</a>
            </div>

            <div className="contact-card">
              <div className="contact-icon-wrapper whatsapp">
                <svg className="contact-icon" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h3 className="contact-title">International Contact</h3>
              <p className="contact-label">WhatsApp</p>
              <a href="https://wa.me/16462759747" target="_blank" rel="noopener noreferrer" className="contact-link">+1 (646) 275-9747</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img
                  src="https://customer-assets.emergentagent.com/job_gym-guru-2/artifacts/bv7uqtnu_Rig%20Solid%20Medium%20Fill.png"
                  alt="V2 Fitness Logo"
                  className="logo-image" />

                <span>{trainerInfo.name}</span>
              </div>
              <p className="body-small">IFBB Pro Body Transformation Specialist</p>
              <p className="body-small">Transforming lives through proven training methods</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Services</h4>
                <ul>
                  <li><a href="#services">Bodybuilding & Muscle Gain</a></li>
                  <li><a href="#services">Weight Loss & Fat Reduction</a></li>
                  <li><a href="#services">Online Coaching</a></li>
                  <li><a href="#services">Competition Prep</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Quick Links</h4>
                <ul>
                  <li><a href="#transformations">Success Stories</a></li>
                  <li><a href="#booking">Book Free Session</a></li>
                  <li><a href="#services">Programs</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="body-small">© 2025 {trainerInfo.name}. All rights reserved. Your transformation starts here.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;